import type { Layout, PlotData } from "plotly.js";
import { PlotCard } from "../PlotCard";
import type { LevelRow } from "./LevelsEditor";
import type { SoilRowDraft } from "./SoilsEditor";
import { buildSoilColourMap, soilColour } from "./soilColours";

interface Props {
  levels: LevelRow[];
  soils: SoilRowDraft[];
  driveLengthM: number | null;
}

/**
 * Visual cross-check for the JF Drive setup form. Renders the level
 * profile (ground / water / tunnel) over chainage with the soil schedule
 * shown as full-height coloured bands. Lets the engineer eyeball whether
 * an auto-fill (or manual entry) lines up before saving.
 *
 * The component is purely a view of the drawer's draft state; it accepts
 * partially-filled rows (cells can be `""`) and silently drops any row
 * where a required value is non-numeric.
 */
export function SchematicPreview({ levels, soils, driveLengthM }: Props) {
  const validLevels = levels
    .map((r) => ({
      ch: numOrNull(r.chainageM),
      g: numOrNull(r.groundM),
      w: numOrNull(r.waterM),
      t: numOrNull(r.tunnelM),
    }))
    .filter(
      (r): r is { ch: number; g: number; w: number; t: number } =>
        r.ch != null && r.g != null && r.w != null && r.t != null,
    )
    .sort((a, b) => a.ch - b.ch);

  const validSoils = soils
    .map((r) => ({
      desc: r.soilType.trim(),
      start: numOrNull(r.startM),
      end: numOrNull(r.endM),
    }))
    .filter(
      (r): r is { desc: string; start: number; end: number } =>
        r.desc.length > 0 && r.start != null && r.end != null && r.end > r.start,
    );

  // Build the colour map from the raw soil rows so the band hue
  // matches what the editor and summary panel show. Using the raw
  // `soils` (not `validSoils`) means even rows that don't render a
  // band still occupy their first-occurrence slot, keeping colours
  // stable while the operator is mid-edit.
  const colourMap = buildSoilColourMap(
    soils.map((s) => ({ soilType: s.soilType })),
  );

  const empty = validLevels.length < 2 && validSoils.length === 0;

  const traces: Partial<PlotData>[] = empty
    ? []
    : [
        {
          x: validLevels.map((r) => r.ch),
          y: validLevels.map((r) => r.g),
          type: "scatter",
          mode: "lines+markers",
          name: "Ground level",
          line: { color: "#8B5A2B", width: 2 },
          marker: { color: "#8B5A2B", size: 5 },
        },
        {
          x: validLevels.map((r) => r.ch),
          y: validLevels.map((r) => r.w),
          type: "scatter",
          mode: "lines+markers",
          name: "Water level",
          line: { color: "#1d4ed8", width: 1.5, dash: "dash" },
          marker: { color: "#1d4ed8", size: 4 },
        },
        {
          x: validLevels.map((r) => r.ch),
          y: validLevels.map((r) => r.t),
          type: "scatter",
          mode: "lines+markers",
          name: "Tunnel level",
          line: { color: "#374151", width: 2.5 },
          marker: { color: "#374151", size: 5 },
        },
      ];

  const shapes: NonNullable<Layout["shapes"]> = validSoils.map((s) => ({
    type: "rect",
    xref: "x",
    yref: "paper",
    x0: s.start,
    x1: s.end,
    y0: 0,
    y1: 1,
    // Colour by name, not by row index, so a "Clay" band that
    // reappears below a "Sand" lens draws in the same hue and reads
    // as one logical layer.
    fillcolor: soilColour(s.desc, colourMap),
    opacity: 0.22,
    line: { width: 0 },
    layer: "below",
  }));

  const annotations: NonNullable<Layout["annotations"]> = empty
    ? [
        {
          text:
            "Schematic preview will appear here once levels or soils are entered.",
          showarrow: false,
          xref: "paper",
          yref: "paper",
          x: 0.5,
          y: 0.5,
          font: { size: 12, color: "#8b8b8b" },
        },
      ]
    : validSoils.map((s) => ({
        text: s.desc,
        showarrow: false,
        x: (s.start + s.end) / 2,
        xref: "x",
        y: 1,
        yref: "paper",
        yanchor: "bottom",
        font: { size: 10, color: "#475569" },
      }));

  const layout: Partial<Layout> = {
    xaxis: {
      title: { text: "Chainage (m)" },
      range: driveLengthM ? [0, driveLengthM] : undefined,
      zeroline: false,
    },
    yaxis: {
      title: { text: "Level (mAOD)" },
      zeroline: false,
    },
    shapes,
    annotations,
    showlegend: true,
    legend: {
      orientation: "h",
      y: -0.28,
      x: 0,
      font: { size: 10 },
    },
    margin: { l: 56, r: 16, t: 16, b: 56 },
  };

  return (
    <PlotCard
      title="Schematic preview"
      subtitle="Levels and soil layers as currently entered in the form below."
      data={traces}
      layout={layout}
      height={280}
      uirevision="jf-schematic-preview"
    />
  );
}

function numOrNull(v: number | ""): number | null {
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}
